/**
 * Bonus Suitcase Overlay
 *
 * Fullscreen overlay that shows the 3D suitcase scene with
 * instructional prompts and prize reveal UI. Opened from the
 * gift icon on the home screen.
 */

import { EventEmitter } from '../../engine/core/EventEmitter';
import { BonusSuitcaseScene } from '../scenes/BonusSuitcaseScene';

export interface BonusSuitcaseResult {
  amount: number;
  claimed: boolean;
}

export class BonusSuitcaseOverlay extends EventEmitter {
  private overlay: HTMLElement;
  private sceneContainer: HTMLElement;
  private promptEl: HTMLElement;
  private prizeEl: HTMLElement;
  private closeBtn: HTMLElement;
  private scene: BonusSuitcaseScene | null = null;
  private streakInfoEl: HTMLElement;
  private prizeAmount: number;
  private streakMultiplier: number = 1;
  /** Emitted 'opened' yet? The bonus is committed (claimed) only when the case
   *  actually opens — anti-abuse, and so peeking + leaving keeps it claimable. */
  private hasCommitted = false;

  constructor(private container: HTMLElement) {
    super();
    this.prizeAmount = 0;

    // Build DOM
    this.overlay = document.createElement('div');
    this.overlay.className = 'bonus-overlay';
    this.overlay.innerHTML = `
      <div class="bonus-overlay-bg"></div>
      <div class="bonus-godrays"></div>
      <div class="bonus-scene-container"></div>
      <div class="bonus-prompt">OPEN THE CASE</div>
      <div class="bonus-prize bonus-hidden">
        <div class="bonus-prize-label">YOU WON</div>
        <div class="bonus-prize-amount">$0</div>
        <div class="bonus-streak-info"></div>
        <button class="bonus-claim-btn">COLLECT</button>
      </div>
      <button class="bonus-close-btn">&times;</button>
    `;

    this.sceneContainer = this.overlay.querySelector('.bonus-scene-container')!;
    this.promptEl = this.overlay.querySelector('.bonus-prompt')!;
    this.prizeEl = this.overlay.querySelector('.bonus-prize')!;
    this.streakInfoEl = this.overlay.querySelector('.bonus-streak-info')!;
    this.closeBtn = this.overlay.querySelector('.bonus-close-btn')!;

    // Events
    this.closeBtn.addEventListener('click', () => this.close(false));
    this.overlay.querySelector('.bonus-claim-btn')!.addEventListener('click', () => this.close(true));

    // Inject styles
    this.injectStyles();
  }

  /**
   * Open the overlay and start the suitcase experience.
   * @param prizeAmount The amount the player will win.
   */
  async open(prizeAmount: number, cashStackCount?: number, streakMultiplier?: number): Promise<void> {
    this.prizeAmount = prizeAmount;
    this.streakMultiplier = streakMultiplier ?? 1;
    this.container.appendChild(this.overlay);

    // Fade in
    requestAnimationFrame(() => {
      this.overlay.classList.add('active');
    });

    // Create 3D scene
    this.scene = new BonusSuitcaseScene(this.sceneContainer, prizeAmount, cashStackCount);

    // Listen to scene events
    this.scene.on('state', (data: unknown) => {
      const { state } = data as { state: string };
      switch (state) {
        case 'locked':
          this.promptEl.textContent = 'OPEN THE CASE';
          this.promptEl.classList.remove('bonus-hidden');
          break;
        case 'left_unlocked':
        case 'right_unlocked':
          this.promptEl.textContent = 'OPEN THE CASE';
          break;
        case 'unlocked':
          this.promptEl.textContent = 'OPEN THE CASE';
          this.promptEl.classList.add('pulse');
          break;
        case 'opening':
          this.promptEl.classList.add('bonus-hidden');
          this.commitOpen();
          break;
        case 'open':
          this.commitOpen();
          break;
      }
    });

    this.scene.on('reject', () => {
      this.promptEl.textContent = 'UNLOCK CASE FIRST';
      this.promptEl.classList.add('shake');
      setTimeout(() => this.promptEl.classList.remove('shake'), 500);
    });

    this.scene.on('godrays', () => {
      this.overlay.querySelector('.bonus-godrays')!.classList.add('active');
    });

    this.scene.on('prize:revealed', (data: unknown) => {
      this.commitOpen(); // fallback in case the 'opening' state was missed
      const { amount } = data as { amount: number };
      this.showPrize(amount);
    });

    this.scene.on('claim', () => {
      this.close(true);
    });

    await this.scene.ready;
  }

  /** Fire 'opened' exactly once — the moment the player commits to opening the
   *  case. The host claims the bonus here (not on overlay open, not on COLLECT). */
  private commitOpen(): void {
    if (this.hasCommitted) return;
    this.hasCommitted = true;
    this.emit('opened', undefined);
  }

  private showPrize(amount: number): void {
    this.promptEl.classList.add('bonus-hidden');
    this.prizeEl.classList.remove('bonus-hidden');

    const amountEl = this.prizeEl.querySelector('.bonus-prize-amount')!;
    this.animateCounter(amountEl, amount);

    // Show streak bonus info if multiplier > 1
    if (this.streakMultiplier > 1) {
      const baseAmount = Math.round(amount / this.streakMultiplier);
      const bonusAmount = amount - baseAmount;
      const multLabel = `x${this.streakMultiplier}`;
      this.streakInfoEl.textContent = `includes $${bonusAmount.toLocaleString()} from ${multLabel} streak bonus`;
    } else {
      this.streakInfoEl.textContent = '';
    }
  }

  private animateCounter(el: Element, target: number): void {
    const duration = 1200;
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      // Ease-out
      const eased = 1 - Math.pow(1 - t, 3);
      const current = Math.round(target * eased);
      el.textContent = `$${current.toLocaleString()}`;

      if (t < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  private close(claimed: boolean): void {
    this.overlay.classList.remove('active');

    // Wait for fade out
    setTimeout(() => {
      this.scene?.dispose();
      this.scene = null;
      this.overlay.remove();

      // Reset state
      this.promptEl.classList.remove('bonus-hidden', 'pulse');
      this.prizeEl.classList.add('bonus-hidden');
      this.overlay.querySelector('.bonus-godrays')?.classList.remove('active');

      this.emit('close', {
        amount: this.prizeAmount,
        claimed,
      } as BonusSuitcaseResult);
    }, 400);
  }

  private injectStyles(): void {
    if (document.getElementById('bonus-suitcase-styles')) return;

    const style = document.createElement('style');
    style.id = 'bonus-suitcase-styles';
    style.textContent = `
      .bonus-overlay {
        position: fixed;
        inset: 0;
        z-index: 900;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.4s ease;
      }

      .bonus-overlay.active {
        opacity: 1;
        pointer-events: all;
      }

      .bonus-overlay-bg {
        position: absolute;
        inset: 0;
        background: radial-gradient(ellipse at 50% 40%, rgba(20, 10, 30, 0.95), rgba(5, 5, 8, 0.98));
      }

      .bonus-godrays {
        position: absolute;
        z-index: 0;
        opacity: 0;
        pointer-events: none;
        width: 110vmax;
        height: 110vmax;
        top: 45%;
        left: 50%;
        translate: -50% -50%;
        background: url('/assets/images/rays.png') center / cover no-repeat;
        transition: opacity 0.8s ease;
      }

      .bonus-godrays.active {
        opacity: 0.25;
        animation: bonus-godrays-spin 20s linear infinite;
      }

      @keyframes bonus-godrays-spin {
        from { rotate: 0deg; }
        to { rotate: 360deg; }
      }

      .bonus-scene-container {
        position: relative;
        width: 100%;
        height: 55vh;
        z-index: 1;
        flex-shrink: 0;
      }

      .bonus-prompt {
        position: relative;
        z-index: 2;
        font-family: var(--font-display);
        font-size: 22px;
        letter-spacing: 4px;
        color: rgba(255, 215, 0, 0.9);
        text-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
        text-align: center;
        margin-top: 20px;
        transition: opacity 0.3s ease, transform 0.3s ease;
      }

      .bonus-prompt.bonus-hidden {
        opacity: 0;
        visibility: hidden;
        transform: translateY(10px);
        pointer-events: none;
      }

      .bonus-prompt.pulse {
        animation: bonus-prompt-pulse 1.2s ease-in-out infinite;
      }

      @keyframes bonus-prompt-pulse {
        0%, 100% { opacity: 0.8; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.05); }
      }

      .bonus-prompt.shake {
        animation: bonus-prompt-shake 0.4s ease;
        color: rgba(255, 100, 100, 0.9);
        text-shadow: 0 0 20px rgba(255, 100, 100, 0.3);
      }

      @keyframes bonus-prompt-shake {
        0%, 100% { transform: translateX(0); }
        15% { transform: translateX(-8px); }
        30% { transform: translateX(8px); }
        45% { transform: translateX(-6px); }
        60% { transform: translateX(6px); }
        75% { transform: translateX(-3px); }
        90% { transform: translateX(3px); }
      }

      .bonus-prize {
        position: relative;
        z-index: 2;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        margin-top: 20px;
        transition: opacity 0.4s ease, transform 0.4s ease, visibility 0s 0.4s;
      }

      .bonus-prize:not(.bonus-hidden) {
        transition: opacity 0.4s ease, transform 0.4s ease, visibility 0s 0s;
      }

      .bonus-prize.bonus-hidden {
        opacity: 0;
        visibility: hidden;
        transform: translateY(20px) scale(0.9);
        pointer-events: none;
      }

      .bonus-prize-label {
        font-family: var(--font-body);
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 3px;
        color: rgba(255, 255, 255, 0.5);
        text-transform: uppercase;
      }

      .bonus-prize-amount {
        font-family: var(--font-display);
        font-size: 48px;
        letter-spacing: 2px;
        color: var(--green);
        text-shadow: 0 0 30px rgba(0, 255, 136, 0.4), 0 0 60px rgba(0, 255, 136, 0.15);
      }

      .bonus-streak-info {
        font-family: var(--font-body);
        font-size: 20px;
        font-weight: 500;
        color: rgba(255, 0, 128, 0.8);
        letter-spacing: 0.5px;
        line-height: 3px;
        margin-top: 2px;
        min-height: 16px;
      }

      .bonus-claim-btn {
        margin-top: 12px;
        padding: 14px 48px;
        background: linear-gradient(135deg, var(--gold), var(--gold-dim));
        border: none;
        border-radius: 14px;
        font-family: var(--font-display);
        font-size: 22px;
        letter-spacing: 3px;
        color: var(--dark-bg);
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
        -webkit-tap-highlight-color: transparent;
      }

      .bonus-claim-btn:active {
        transform: scale(0.96);
      }

      .bonus-close-btn {
        position: absolute;
        top: max(16px, env(safe-area-inset-top));
        right: 16px;
        z-index: 3;
        width: 40px;
        height: 40px;
        background: rgba(255, 255, 255, 0.06);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 22px;
        color: rgba(255, 255, 255, 0.5);
        cursor: pointer;
        transition: all 0.2s;
        -webkit-tap-highlight-color: transparent;
      }

      .bonus-close-btn:active {
        transform: scale(0.9);
        background: rgba(255, 255, 255, 0.1);
      }
    `;
    document.head.appendChild(style);
  }

  destroy(): void {
    this.scene?.dispose();
    this.scene = null;
    this.overlay.remove();
    this.removeAllListeners();
  }
}
